import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, X, Pencil, FileText } from 'lucide-react';

// One registration, admin view: every answer (uploads open as links), signed
// agreements, the group, and the edit history. "Edit" changes answers / group,
// previews the before/after + fee effect, then saves (consent required when the
// family's bill goes up). Backend: /api/reg-submissions/{org}/submissions/{sid}/detail|edit.
const API_URL = process.env.REACT_APP_BACKEND_URL;
const LINK_RE = /^(.*) \((https?:\/\/[^)]+\/uploads\/[^)]+)\)$/;

const showValue = (v) => {
  if (v === null || v === undefined || v === '') return <span className="text-slate-400">—</span>;
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  const m = String(v).match(LINK_RE);
  if (m) return <a href={m[2]} target="_blank" rel="noreferrer" className="text-amber-600 underline inline-flex items-center gap-1"><FileText className="h-3 w-3" />{m[1]}</a>;
  return String(v);
};

export default function RegistrationDetailsPanel({ orgId, sid, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(false);
  const [vals, setVals] = useState({});
  const [groupId, setGroupId] = useState('');
  const [preview, setPreview] = useState(null);
  const [consent, setConsent] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const { data } = await axios.get(`${API_URL}/api/reg-submissions/${orgId}/submissions/${sid}/detail`);
    setD(data); setGroupId(data.submission.group_id || '');
    const v = {}; (data.answers || []).forEach(a => { v[a.key] = a.value; }); setVals(v);
  };
  useEffect(() => { load().catch(() => alert('Could not load registration')); }, [orgId, sid]); // eslint-disable-line react-hooks/exhaustive-deps

  const changed = () => {
    const out = {};
    (d?.answers || []).forEach(a => { if (JSON.stringify(vals[a.key] ?? null) !== JSON.stringify(a.value ?? null)) out[a.key] = vals[a.key]; });
    return out;
  };
  const body = (extra = {}) => ({ answers: changed(), group_id: groupId, reason, family_consent: consent, ...extra });
  const doPreview = async () => {
    setBusy(true);
    try { const { data } = await axios.patch(`${API_URL}/api/reg-submissions/${orgId}/submissions/${sid}/edit`, body({ preview: true })); setPreview(data); }
    catch (e) { alert(e.response?.data?.detail || 'Preview failed'); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API_URL}/api/reg-submissions/${orgId}/submissions/${sid}/edit`, body());
      setEdit(false); setPreview(null); setConsent(false); setReason(''); await load(); onSaved && onSaved();
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };

  const sp = d?.submission?.staged_player || {};
  const input = 'w-full rounded border p-1 text-sm dark:bg-slate-700 dark:border-slate-600';
  let lastSection = null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose} data-testid="reg-details">
      <div className="h-full w-full max-w-2xl overflow-y-auto bg-white dark:bg-slate-900 border-l dark:border-slate-700 p-4 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold">{sp.first_name} {sp.last_name} <span className="text-xs text-slate-400 font-normal">{d?.submission?.status} · {d?.submission?.selection_status}</span></h3>
          <div className="flex items-center gap-2">
            {d && !edit && <button className="text-xs border rounded px-2 py-1 flex items-center gap-1" onClick={() => setEdit(true)} data-testid="reg-edit"><Pencil className="h-3 w-3" />Edit</button>}
            <button onClick={onClose}><X className="h-5 w-5" /></button>
          </div>
        </div>
        {!d ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div> : (<>
          <div className="text-sm space-y-1">
            <div><span className="text-slate-400">Group:</span>{' '}
              {edit ? (
                <select className="rounded border p-1 text-sm dark:bg-slate-700 dark:border-slate-600" value={groupId} onChange={e => setGroupId(e.target.value)} data-testid="reg-edit-group">
                  {d.groups.map(g => <option key={g.id} value={g.id}>{g.name}{g.fee ? ` — $${g.fee}` : ''}</option>)}
                </select>
              ) : (d.groups.find(g => g.id === d.submission.group_id) || {}).name}
            </div>
            <div><span className="text-slate-400">DOB / gender:</span> {sp.birthday || '—'} · {sp.gender || '—'}</div>
            <div><span className="text-slate-400">Fee:</span> ${Number(d.submission.fee || 0).toFixed(2)} · {d.submission.payment_status}</div>
            {d.submission.source === 'external' && (
              <div className="rounded border border-sky-300 p-2 space-y-1" data-testid="ext-details">
                <div className="text-xs font-semibold text-sky-700">From the external platform (read-only; updated each sync)</div>
                {(d.submission.staged_family?.contacts || []).map((c, i) => <div key={i} className="text-xs">{c.is_primary ? 'Primary' : 'Secondary'} contact: {c.name} · {c.relationship} · {c.email} · {c.phone}</div>)}
                {d.submission.external_payment && (() => { const x = d.submission.external_payment; const p = x.plan || {}; return (
                  <div className="text-xs space-y-0.5">
                    <div>Total ${Number(x.total || 0).toFixed(2)} · paid ${Number(x.paid || 0).toFixed(2)} · balance ${Number(x.balance || 0).toFixed(2)} · {x.status}{(x.discounts || []).length ? ` · discounts: ${x.discounts.join(', ')}` : ''}{x.order_number ? ` · order ${x.order_number}` : ''}</div>
                    {p.payment_method && <div>Card: {p.payment_method.account_type || p.payment_method.type} ••{p.payment_method.last4}</div>}
                    {(p.installments || []).map((i, k) => <div key={k}>Installment {k + 1}: ${Number(i.amount).toFixed(2)} · {i.status} · {String(i.scheduled_date || '').slice(0, 10)}{(i.transactions || []).length ? ` · ${i.transactions.length} transaction(s)` : ''}</div>)}
                    {(p.line_items || []).map((li, k) => <div key={`l${k}`} className={li.canceled ? 'line-through' : ''}>{li.name}: ${Number(li.amount).toFixed(2)}</div>)}
                  </div>); })()}
                {(d.submission.shadow_diff || []).length > 0 && <div className="text-xs text-red-600">OL would differ: {d.submission.shadow_diff.join('; ')}</div>}
              </div>
            )}
          </div>

          <div className="space-y-1">
            <h4 className="text-xs font-semibold uppercase text-slate-400">Answers</h4>
            {d.answers.map(a => {
              const head = a.section && a.section !== lastSection ? <div key={`s-${a.key}`} className="text-xs font-semibold pt-2">{a.section}</div> : null;
              if (a.section) lastSection = a.section;
              return (
                <React.Fragment key={a.key}>
                  {head}
                  <div className="grid grid-cols-5 gap-2 text-sm py-1 border-b dark:border-slate-800" data-testid={`answer-${a.key}`}>
                    <div className="col-span-2 text-slate-500">{a.label}{a.scope === 'family' ? ' (family)' : ''}</div>
                    <div className="col-span-3">
                      {edit && a.qtype !== 'file' ? (
                        (a.options || []).length && !a.allow_multiple ? (
                          <select className={input} value={vals[a.key] ?? ''} onChange={e => setVals(v => ({ ...v, [a.key]: e.target.value }))}>
                            <option value="">—</option>{a.options.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : (
                          <input className={input} value={Array.isArray(vals[a.key]) ? vals[a.key].join(', ') : (vals[a.key] ?? '')}
                            onChange={e => setVals(v => ({ ...v, [a.key]: a.allow_multiple ? e.target.value.split(',').map(x => x.trim()).filter(Boolean) : e.target.value }))} />
                        )
                      ) : showValue(a.value)}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {edit && (
            <div className="border rounded p-3 space-y-2 dark:border-slate-700" data-testid="reg-edit-box">
              <input className={input} placeholder="Reason for the change" value={reason} onChange={e => setReason(e.target.value)} />
              {!preview ? (
                <div className="flex gap-2">
                  <button className="px-3 py-1 rounded bg-amber-600 text-white text-sm" disabled={busy} onClick={doPreview} data-testid="reg-edit-review">Review changes</button>
                  <button className="px-3 py-1 text-sm" onClick={() => { setEdit(false); load(); }}>Cancel</button>
                </div>
              ) : (
                <div className="space-y-2 text-sm" data-testid="reg-edit-preview">
                  {preview.diff.length === 0 && <p className="text-slate-400">No changes.</p>}
                  {preview.diff.map((x, i) => <div key={i}><b>{x.field}</b>: {showValue(x.before)} → {showValue(x.after)}</div>)}
                  <div>Fee: ${preview.effect.old_fee.toFixed(2)} → ${preview.effect.new_fee.toFixed(2)}{preview.effect.has_bill && Math.abs(preview.effect.delta) >= 0.01 ? ' (the family\'s bill is adjusted by the difference)' : ''}</div>
                  {preview.effect.has_bill && preview.effect.delta > 0 && (
                    <label className="flex items-center gap-2"><input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} data-testid="reg-edit-consent" /> The family agreed to the higher amount</label>
                  )}
                  <p className="text-xs text-slate-400">The family is emailed the before/after.</p>
                  <div className="flex gap-2">
                    <button className="px-3 py-1 rounded bg-green-600 text-white" disabled={busy || !preview.diff.length} onClick={save} data-testid="reg-edit-save">Save</button>
                    <button className="px-3 py-1" onClick={() => setPreview(null)}>Back</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {d.agreements.length > 0 && (
            <div className="space-y-1">
              <h4 className="text-xs font-semibold uppercase text-slate-400">Agreements</h4>
              {d.agreements.map((a, i) => <div key={i} className="text-sm flex justify-between border-b dark:border-slate-800 py-1"><span>{a.title}</span><span>{a.signed ? `Signed (${a.signed})` : <span className="text-red-500">Not signed</span>}</span></div>)}
            </div>
          )}

          <div className="space-y-1">
            <h4 className="text-xs font-semibold uppercase text-slate-400">History</h4>
            {(d.history || []).length === 0 && <p className="text-xs text-slate-400">No changes yet.</p>}
            {(d.history || []).map((h, i) => (
              <div key={i} className="text-xs py-1 border-b dark:border-slate-800" data-testid="reg-history-row">
                <span className="text-slate-400">{String(h.created_at || h.at || '').slice(0, 16).replace('T', ' ')} · {h.actor_name || ''}</span> — {h.action}{h.summary ? `: ${h.summary}` : ''}
              </div>
            ))}
          </div>
        </>)}
      </div>
    </div>
  );
}
